import React, { useState, useEffect, useContext } from "react";
import { SlidersHorizontal, Copy, Building2 } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import { FieldConfigurationContext } from "../../../../lib/context/FieldConfigurationContext";
import DetailHeader from "../components/DetailHeader";
import FormFooter from "../components/FormFooter";
import AuditHistory from "../components/AuditHistory";
import Button from "../../../common/Button";
import TextInput from "../../../common/TextInput";
import TextArea from "../../../common/TextArea";
import Validation from "../../../common/Validation";
import CopyGroupModal from "./AccessGroupDetail/CopyGroupModal";
import JobsFilterModal from "./JobsFilterModal";
import EmployeesFilterModal from "./EmployeesFilterModal";
import EquipmentFilterModal from "./EquipmentFilterModal";
import PayrollDepartmentsFilterModal from "./PayrollDepartmentsFilterModal";
import WorkLocationsFilterModal from "./WorkLocationsFilterModal";
import CostTypesFilterModal from "./CostTypesFilterModal";
import AbsenceCodesFilterModal from "./AbsenceCodesFilterModal";
import ExpenseTypesFilterModal from "./ExpenseTypesFilterModal";
import FieldLogsFilterModal from "./FieldLogsFilterModal";
import WorkerClassesFilterModal from "./WorkerClassesFilterModal";
import PayrollUnionsFilterModal from "./PayrollUnionsFilterModal";
import WorkerCompCodesFilterModal from "./WorkerCompCodesFilterModal";
import VendorsFilterModal from "./VendorsFilterModal";
import ServiceWorkOrdersFilterModal from "./ServiceWorkOrdersFilterModal";
import InventoryItemsFilterModal from "./InventoryItemsFilterModal";
import {
  getAccessCodeLabel,
  getActiveCompanyOptions,
} from "./AccessGroupsList";
import {
  FILTERABLE_RESOURCES,
  getEnabledResources,
  getFilterTagModifier,
  getVisibleResources,
} from "./accessGroupResources";
import "../style.css";

// api_identity's AuditSnapshots.logFilterChange stores each filter change
// under the field key `filters.<resource>` (e.g. "filters.svcwrko"), so the
// audit history's default titleization renders the raw resource key
// ("Svcwrko") instead of the friendly label shown elsewhere on this screen.
const FILTER_AUDIT_FIELD_LABELS = Object.fromEntries(
  FILTERABLE_RESOURCES.map((r) => [`filters.${r.key}`, r.label])
);

function showsInboundToggle(resource, mode) {
  return !!resource.hasInboundToggle && mode === "all";
}

export default function AccessGroupDetail({
  group,
  onBack,
  onUpdate,
  startEditing = false,
}) {
  const { updateGroup, companies, licenses, fetchLicenses } = useContext(
    FieldConfigurationContext
  );
  const [isEditing, setIsEditing] = useState(startEditing);
  const [displayGroup, setDisplayGroup] = useState(group);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySuccessMessage, setCopySuccessMessage] = useState(null);
  const [formData, setFormData] = useState({
    name: group?.name || "",
    description: group?.description || "",
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [auditRefreshKey, setAuditRefreshKey] = useState(0);
  const [memberCount, setMemberCount] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [filters, setFilters] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  // Staged, unsaved edits to permission rows, keyed by resource. Only
  // resources the user actually touched appear here; everything else keeps
  // showing its last-saved value from `filters` until the outer Save/Done
  // is clicked.
  const [pendingFilters, setPendingFilters] = useState({});
  const [jobsModalOpen, setJobsModalOpen] = useState(false);
  const [employeesModalOpen, setEmployeesModalOpen] = useState(false);
  const [equipmentModalOpen, setEquipmentModalOpen] = useState(false);
  const [prdeptModalOpen, setPrdeptModalOpen] = useState(false);
  const [wlocModalOpen, setWlocModalOpen] = useState(false);
  const [jcctModalOpen, setJcctModalOpen] = useState(false);
  const [absenceCodesModalOpen, setAbsenceCodesModalOpen] = useState(false);
  const [expenseTypesModalOpen, setExpenseTypesModalOpen] = useState(false);
  const [fieldLogsModalOpen, setFieldLogsModalOpen] = useState(false);
  const [prclassModalOpen, setPrclassModalOpen] = useState(false);
  const [prunionModalOpen, setPrunionModalOpen] = useState(false);
  const [prwcompModalOpen, setPrwcompModalOpen] = useState(false);
  const [venModalOpen, setVenModalOpen] = useState(false);
  const [svcwrkoModalOpen, setSvcwrkoModalOpen] = useState(false);
  const [invModalOpen, setInvModalOpen] = useState(false);

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  useEffect(() => {
    if (!group?.uuid) return;
    setMembersLoading(true);
    FieldConfigurationAPI.getGroup(group.uuid, group.company_uuid).then(
      (response) => {
        if (response.status === 200 && response.data?.member_count != null) {
          setMemberCount(response.data.member_count);
        } else {
          setMemberCount(null);
        }
        setMembersLoading(false);
      }
    );
  }, [group?.uuid]);

  useEffect(() => {
    if (!group?.uuid) return;
    setFiltersLoading(true);
    FieldConfigurationAPI.getGroupFilters(group.uuid, group.company_uuid).then(
      (response) => {
        if (response.status === 200 && Array.isArray(response.data)) {
          setFilters(response.data);
        }
        setFiltersLoading(false);
      }
    );
  }, [group?.uuid]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = "Name is required.";
    }
    if (!formData.description?.trim()) {
      newErrors.description = "Description is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleStartEdit = () => {
    setFormData({
      name: displayGroup?.name || "",
      description: displayGroup?.description || "",
    });
    setErrors({});
    setSaveError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (!window.confirm("Are you sure you want to cancel?")) return;
    setFormData({
      name: displayGroup?.name || "",
      description: displayGroup?.description || "",
    });
    setErrors({});
    setSaveError(null);
    setPendingFilters({});
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!validate()) {
      setSaveError("Please fix the highlighted fields before saving.");
      const el = document.querySelector(".role-detail__fields");
      if (el && el.scrollIntoView) el.scrollIntoView({ block: "center" });
      return;
    }
    setIsSaving(true);
    setSaveError(null);

    const payload = {
      name: formData.name.trim(),
      description: formData.description?.trim() || "",
      access_codes: displayGroup?.access_codes || [],
    };

    const result = await updateGroup(
      displayGroup.uuid,
      payload,
      displayGroup?.company_uuid
    );

    if (!result?.success) {
      setIsSaving(false);
      setSaveError(result?.error || "Failed to save access group.");
      return;
    }

    setDisplayGroup(result.data);
    onUpdate && onUpdate(result.data);

    const pendingEntries = Object.entries(pendingFilters);
    if (pendingEntries.length === 0) {
      setIsSaving(false);
      setIsEditing(false);
      setAuditRefreshKey((k) => k + 1);
      return;
    }

    const outcomes = await Promise.all(
      pendingEntries.map(([resourceKey, pending]) =>
        FieldConfigurationAPI.upsertGroupFilter(
          group.uuid,
          {
            resource: resourceKey,
            mode: pending.mode,
            rules: pending.rules || [],
          },
          group.company_uuid
        ).then((res) => ({ resourceKey, res }))
      )
    );

    const failedKeys = new Set();
    const saved = [];
    outcomes.forEach(({ resourceKey, res }) => {
      if (res.status === 200 || res.status === 201) {
        saved.push(res.data);
      } else {
        failedKeys.add(resourceKey);
      }
    });

    if (saved.length > 0) {
      setFilters((prev) => {
        const savedKeys = new Set(saved.map((f) => f.resource));
        const without = prev.filter((f) => !savedKeys.has(f.resource));
        return [...without, ...saved];
      });
    }

    setIsSaving(false);

    if (failedKeys.size > 0) {
      setPendingFilters((prev) => {
        const remaining = {};
        failedKeys.forEach((key) => {
          remaining[key] = prev[key];
        });
        return remaining;
      });
      setSaveError("Failed to save some permission changes. Please try again.");
      return;
    }

    setPendingFilters({});
    setIsEditing(false);
    setAuditRefreshKey((k) => k + 1);
  };

  const handleModeChange = (resourceKey, newMode) => {
    const existing =
      pendingFilters[resourceKey] ||
      filters.find((f) => f.resource === resourceKey);
    setPendingFilters((prev) => ({
      ...prev,
      [resourceKey]: { mode: newMode, rules: existing?.rules || [] },
    }));
  };

  const handleInboundToggle = (resourceKey, checked) => {
    const existing =
      pendingFilters[resourceKey] ||
      filters.find((f) => f.resource === resourceKey);
    setPendingFilters((prev) => ({
      ...prev,
      [resourceKey]: {
        mode: existing?.mode || "none",
        rules: [{ enable_inbound: checked }],
      },
    }));
  };

  const handleFilterSave = (updatedFilter) => {
    setPendingFilters((prev) => ({
      ...prev,
      [updatedFilter.resource]: {
        mode: updatedFilter.mode,
        rules: updatedFilter.rules,
      },
    }));
    setJobsModalOpen(false);
    setEmployeesModalOpen(false);
    setEquipmentModalOpen(false);
    setPrdeptModalOpen(false);
    setWlocModalOpen(false);
    setJcctModalOpen(false);
    setAbsenceCodesModalOpen(false);
    setExpenseTypesModalOpen(false);
    setFieldLogsModalOpen(false);
    setPrclassModalOpen(false);
    setPrunionModalOpen(false);
    setPrwcompModalOpen(false);
    setVenModalOpen(false);
    setSvcwrkoModalOpen(false);
    setInvModalOpen(false);
  };

  // Returns the record to display for a resource: the staged edit if the
  // user has touched it this edit session, otherwise the last-saved value.
  // Returns the same object reference across renders when nothing changed,
  // so modals hydrating from it in a `[open, filter]` effect don't re-fire.
  function getEffectiveFilter(resourceKey) {
    return (
      pendingFilters[resourceKey] ||
      filters.find((f) => f.resource === resourceKey)
    );
  }

  function openFilterModal(resourceKey) {
    if (resourceKey === "jobs") setJobsModalOpen(true);
    else if (resourceKey === "employees") setEmployeesModalOpen(true);
    else if (resourceKey === "equipment") setEquipmentModalOpen(true);
    else if (resourceKey === "prdept") setPrdeptModalOpen(true);
    else if (resourceKey === "wloc") setWlocModalOpen(true);
    else if (resourceKey === "jcct") setJcctModalOpen(true);
    else if (resourceKey === "absence_codes") setAbsenceCodesModalOpen(true);
    else if (resourceKey === "expense_types") setExpenseTypesModalOpen(true);
    else if (resourceKey === "field_logs") setFieldLogsModalOpen(true);
    else if (resourceKey === "prclass") setPrclassModalOpen(true);
    else if (resourceKey === "prunion") setPrunionModalOpen(true);
    else if (resourceKey === "prwcomp") setPrwcompModalOpen(true);
    else if (resourceKey === "ven") setVenModalOpen(true);
    else if (resourceKey === "svcwrko") setSvcwrkoModalOpen(true);
    else if (resourceKey === "inv") setInvModalOpen(true);
  }

  const accessCodes = displayGroup.access_codes || [];
  const groupType = displayGroup.group_type || "app";
  const hasExpenseLicense = (licenses?.data?.expenses?.limit || 0) > 0;
  // CE shows API Expenses only when the customer has expense licenses.
  const visibleResources = getVisibleResources(groupType).filter(
    (r) => r.key !== "apiexpenses" || hasExpenseLicense
  );

  const enabledResources = getEnabledResources(
    visibleResources,
    getEffectiveFilter
  );

  const companyOptions = getActiveCompanyOptions(companies);
  const isMultiCompany = companyOptions.length > 1;
  const companyName =
    companies?.data?.[displayGroup.company_uuid] || displayGroup.company_uuid;

  const canCopyToCompany = companyOptions.length > 0;

  const handleCopied = (newGroup, targetCompanyName) => {
    setCopyModalOpen(false);
    setCopySuccessMessage(`Copied "${newGroup.name}" to ${targetCompanyName}.`);
  };

  return (
    <div className="role-detail">
      {isEditing ? (
        <DetailHeader
          title={displayGroup.name}
          subtitle={
            isMultiCompany && (
              <>
                <Building2 size={14} />
                <span>{companyName}</span>
              </>
            )
          }
          onBack={handleCancelEdit}
          isEditing={false}
        />
      ) : (
        <DetailHeader
          title={displayGroup.name}
          subtitle={
            isMultiCompany && (
              <>
                <Building2 size={14} />
                <span>{companyName}</span>
              </>
            )
          }
          onBack={onBack}
          onEdit={handleStartEdit}
          isEditing={false}
          backLabel="Back to Access Groups"
          editLabel="Edit Access Group"
        />
      )}

      <div className="role-detail__content">
        <div className="role-detail__section role-detail__section--info">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h3 className="role-detail__section-title">Group Information</h3>
            {!isEditing && canCopyToCompany && (
              <Button
                className="button--secondary"
                onClick={() => {
                  setCopySuccessMessage(null);
                  setCopyModalOpen(true);
                }}
              >
                <Copy size={16} style={{ marginRight: "6px" }} />
                Copy
              </Button>
            )}
          </div>
          {copySuccessMessage && (
            <p style={{ color: "#16a34a", fontSize: "14px" }}>
              {copySuccessMessage}
            </p>
          )}
          {saveError && <Validation>{saveError}</Validation>}
          <div className="role-detail__fields">
            <div className="role-detail__field">
              <label className="role-detail__label">Name</label>
              {isEditing ? (
                <>
                  <TextInput
                    required
                    value={formData.name}
                    onChange={(e) => handleFieldChange("name", e.target.value)}
                    placeholder="Enter group name"
                  />
                  {errors.name && <Validation>{errors.name}</Validation>}
                </>
              ) : (
                <span className="role-detail__value">{displayGroup.name}</span>
              )}
            </div>
            <div className="role-detail__field">
              <label className="role-detail__label">
                Description{" "}
                {isEditing && <span style={{ color: "#e53e3e" }}>*</span>}
              </label>
              {isEditing ? (
                <>
                  <TextArea
                    rows={3}
                    value={formData.description}
                    onChange={(e) =>
                      handleFieldChange("description", e.target.value)
                    }
                    placeholder="Enter group description"
                  />
                  {errors.description && (
                    <Validation>{errors.description}</Validation>
                  )}
                </>
              ) : (
                <span className="role-detail__value">
                  {displayGroup.description || "—"}
                </span>
              )}
            </div>
            <div className="role-detail__field">
              <label className="role-detail__label">Type</label>
              <span className="role-detail__value">
                {groupType === "api" ? "API" : "Field"}
              </span>
            </div>
            <div className="role-detail__field">
              <label className="role-detail__label">Allowed Data</label>
              <div className="role-detail__value">
                {filtersLoading ? (
                  "—"
                ) : enabledResources.length > 0 ? (
                  <div className="access-code-tags">
                    {enabledResources.map(({ resource, mode }) => (
                      <span
                        key={resource.key}
                        className={`access-code-tag access-code-tag--${getFilterTagModifier(
                          mode
                        )}`}
                        title={`${resource.label}: ${mode}`}
                      >
                        {resource.label}
                      </span>
                    ))}
                  </div>
                ) : accessCodes.length > 0 && filters.length === 0 ? (
                  <div className="access-code-tags">
                    {accessCodes.map((code) => (
                      <span key={code} className="access-code-tag">
                        {getAccessCodeLabel(code)}
                      </span>
                    ))}
                  </div>
                ) : (
                  "—"
                )}
              </div>
            </div>
          </div>
        </div>

        {groupType !== "api" && memberCount !== null && (
          <div className="role-detail__section">
            <h3 className="role-detail__section-title">Members</h3>
            {membersLoading ? (
              <p style={{ color: "rgba(100,116,139,1)", fontSize: "14px" }}>
                Loading...
              </p>
            ) : (
              <div className="role-detail__field">
                <span className="role-detail__value">{memberCount}</span>
              </div>
            )}
          </div>
        )}

        <div className="role-detail__section">
          <h3 className="role-detail__section-title">Permissions</h3>
          {filtersLoading ? (
            <p style={{ color: "rgba(100,116,139,1)", fontSize: "14px" }}>
              Loading...
            </p>
          ) : (
            <table className="permissions-table">
              <thead>
                <tr>
                  <th>Module</th>
                  <th>Access Level</th>
                  <th>Filters</th>
                </tr>
              </thead>
              <tbody>
                {visibleResources.map((resource) => {
                  const filterRecord = getEffectiveFilter(resource.key);
                  const mode = filterRecord?.mode || "none";
                  const hasRules = filterRecord?.rules?.length > 0;
                  const showInbound = showsInboundToggle(resource, mode);
                  const showFilterLink =
                    mode === "filtered" && resource.supportsFiltered !== false;
                  return (
                    <tr key={resource.key}>
                      <td className="permissions-table__module">
                        {resource.label}
                      </td>
                      <td>
                        <select
                          className={`permissions-access-select${
                            mode === "filtered"
                              ? " permissions-access-select--filtered"
                              : ""
                          }`}
                          value={mode}
                          disabled={!isEditing}
                          onChange={(e) =>
                            handleModeChange(resource.key, e.target.value)
                          }
                        >
                          <option value="all">All</option>
                          {resource.hasActiveMode && (
                            <option value="active">Active</option>
                          )}
                          <option value="none">None</option>
                          {resource.supportsFiltered !== false && (
                            <option value="filtered">Filtered</option>
                          )}
                        </select>
                      </td>
                      <td>
                        {showInbound && (
                          <label className="permissions-inbound-toggle">
                            <input
                              type="checkbox"
                              checked={
                                !!filterRecord?.rules?.[0]?.enable_inbound
                              }
                              disabled={!isEditing}
                              onChange={(e) =>
                                handleInboundToggle(
                                  resource.key,
                                  e.target.checked
                                )
                              }
                            />
                            <span className="permissions-inbound-toggle__label">
                              <span>Enable</span>
                              <span>Inbound</span>
                            </span>
                          </label>
                        )}
                        {showFilterLink &&
                          (resource.hasFilterModal ? (
                            <button
                              className="permissions-filter-link"
                              onClick={() => openFilterModal(resource.key)}
                            >
                              <SlidersHorizontal size={13} />
                              {isEditing ? (hasRules ? "Edit" : "Set") : "View"}
                            </button>
                          ) : (
                            <span className="permissions-filter-link permissions-filter-link--disabled">
                              <SlidersHorizontal size={13} />
                              {hasRules ? "Edit" : "Set"}
                            </span>
                          ))}
                        {!showInbound && !showFilterLink && "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <AuditHistory
          resource="groups"
          uuid={group.uuid}
          refreshKey={auditRefreshKey}
          createdAt={group.created_at}
          fieldLabels={FILTER_AUDIT_FIELD_LABELS}
        />
      </div>

      {isEditing && saveError && (
        <div style={{ padding: "0 24px" }}>
          <Validation>{saveError}</Validation>
        </div>
      )}

      {isEditing && (
        <FormFooter
          onCancel={handleCancelEdit}
          onSave={handleSave}
          saveDisabled={isSaving}
          saveLabel={isSaving ? "Saving..." : "Save"}
        />
      )}

      <CopyGroupModal
        open={copyModalOpen}
        group={displayGroup}
        companies={companies}
        onClose={() => setCopyModalOpen(false)}
        onCopied={handleCopied}
      />
      <JobsFilterModal
        open={jobsModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("jobs")}
        onSave={handleFilterSave}
        onClose={() => setJobsModalOpen(false)}
        readOnly={!isEditing}
      />
      <EmployeesFilterModal
        open={employeesModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("employees")}
        onSave={handleFilterSave}
        onClose={() => setEmployeesModalOpen(false)}
        readOnly={!isEditing}
      />
      <EquipmentFilterModal
        open={equipmentModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("equipment")}
        onSave={handleFilterSave}
        onClose={() => setEquipmentModalOpen(false)}
        readOnly={!isEditing}
      />
      <PayrollDepartmentsFilterModal
        open={prdeptModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("prdept")}
        onSave={handleFilterSave}
        onClose={() => setPrdeptModalOpen(false)}
        readOnly={!isEditing}
      />
      <WorkLocationsFilterModal
        open={wlocModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("wloc")}
        onSave={handleFilterSave}
        onClose={() => setWlocModalOpen(false)}
        readOnly={!isEditing}
      />
      <CostTypesFilterModal
        open={jcctModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("jcct")}
        onSave={handleFilterSave}
        onClose={() => setJcctModalOpen(false)}
        readOnly={!isEditing}
      />
      <AbsenceCodesFilterModal
        open={absenceCodesModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("absence_codes")}
        onSave={handleFilterSave}
        onClose={() => setAbsenceCodesModalOpen(false)}
        readOnly={!isEditing}
      />
      <ExpenseTypesFilterModal
        open={expenseTypesModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("expense_types")}
        onSave={handleFilterSave}
        onClose={() => setExpenseTypesModalOpen(false)}
        readOnly={!isEditing}
      />
      <FieldLogsFilterModal
        open={fieldLogsModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("field_logs")}
        onSave={handleFilterSave}
        onClose={() => setFieldLogsModalOpen(false)}
        readOnly={!isEditing}
      />
      <WorkerClassesFilterModal
        open={prclassModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("prclass")}
        onSave={handleFilterSave}
        onClose={() => setPrclassModalOpen(false)}
        readOnly={!isEditing}
      />
      <PayrollUnionsFilterModal
        open={prunionModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("prunion")}
        onSave={handleFilterSave}
        onClose={() => setPrunionModalOpen(false)}
        readOnly={!isEditing}
      />
      <WorkerCompCodesFilterModal
        open={prwcompModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("prwcomp")}
        onSave={handleFilterSave}
        onClose={() => setPrwcompModalOpen(false)}
        readOnly={!isEditing}
      />
      <VendorsFilterModal
        open={venModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("ven")}
        onSave={handleFilterSave}
        onClose={() => setVenModalOpen(false)}
        readOnly={!isEditing}
      />
      <ServiceWorkOrdersFilterModal
        open={svcwrkoModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("svcwrko")}
        onSave={handleFilterSave}
        onClose={() => setSvcwrkoModalOpen(false)}
        readOnly={!isEditing}
      />
      <InventoryItemsFilterModal
        open={invModalOpen}
        groupUuid={group.uuid}
        companyUuid={group.company_uuid}
        filter={getEffectiveFilter("inv")}
        onSave={handleFilterSave}
        onClose={() => setInvModalOpen(false)}
        readOnly={!isEditing}
      />
    </div>
  );
}
